import { useEffect } from "react";

export const useGameTradeListeners = (
  socket,
  {
    setPlayerPortfolios,
    setPlayerIndexPortfolios,
    setActionsRemaining,
    setMaxActionsThisTurn,
    setStockOwnershipCounts,
    setStockOwnershipByPlayer,
    setIndexOwnershipByPlayer,
    setPlayerNetWorths,
    showMessage,
    addNotification,
    setLoggedIn
  }
) => {
  useEffect(() => {
    const currentSocket = socket.current;
    if (!currentSocket) return;

    const handlePurchaseResult = ({ success, message, stockName, stockPrice }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] purchase_result received', { success });
      }
      if (success && stockName && stockPrice) {
        addNotification(`✅ Purchased ${stockName} for $${stockPrice}`, null);
      } else if (!success) {
        if (message && message.includes("Room not found")) {
          showMessage("❌ Connection to room lost. Returning to home page...", () => {
            setLoggedIn(false);
          });
        } else {
          showMessage(`❌ ${message}`);
        }
      }
    };

    const handleSellResult = ({ success, message, stockName, salePrice, profitLoss }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] sell_result received', { success });
      }
      if (success && stockName && salePrice !== undefined) {
        const profitText = profitLoss !== undefined 
          ? (profitLoss > 0 ? ` (+$${profitLoss})` : profitLoss < 0 ? ` ($${profitLoss})` : '')
          : '';
        addNotification(`✅ Sold ${stockName} for $${salePrice}${profitText}`, null);
      } else if (!success) {
        if (message && message.includes("Room not found")) {
          showMessage("❌ Connection to room lost. Returning to home page...", () => {
            setLoggedIn(false);
          });
        } else {
          showMessage(`❌ ${message}`);
        }
      }
    };

    const handlePurchaseIndexResult = ({ success, message, indexName, indexPrice }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] purchase_index_result received', { success });
      }
      if (success && indexName && indexPrice) {
        addNotification(`✅ Purchased ${indexName} index for $${indexPrice}`, null);
      } else if (!success) {
        if (message && message.includes("Room not found")) {
          showMessage("❌ Connection to room lost. Returning to home page...", () => {
            setLoggedIn(false);
          });
        } else {
          showMessage(`❌ ${message}`);
        }
      }
    };

    const handleSellIndexResult = ({ success, message, indexName, salePrice, profit }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] sell_index_result received', { success });
      }
      if (success && indexName && salePrice !== undefined) {
        const profitText = profit !== undefined 
          ? (profit > 0 ? ` (+$${profit})` : profit < 0 ? ` ($${profit})` : '')
          : '';
        addNotification(`✅ Sold ${indexName} index for $${salePrice}${profitText}`, null);
      } else if (!success) {
        if (message && message.includes("Room not found")) {
          showMessage("❌ Connection to room lost. Returning to home page...", () => {
            setLoggedIn(false);
          });
        } else {
          showMessage(`❌ ${message}`);
        }
      }
    };

    const handlePortfolioUpdate = ({ playerName, ownedStocks }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] portfolio_update received', { playerName });
      }
      setPlayerPortfolios(prev => ({
        ...prev,
        [playerName]: ownedStocks
      }));
    };

    const handleIndexPortfolioUpdate = ({ playerName, ownedIndexes }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] index_portfolio_update received', { playerName });
      }
      setPlayerIndexPortfolios(prev => ({
        ...prev,
        [playerName]: ownedIndexes
      }));
    };

    const handleActionsUpdate = ({ playerName, actionsRemaining: count, maxActionsThisTurn: maxForTurn }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] actions_update received', { playerName, count, maxActionsThisTurn: maxForTurn });
      }
      setActionsRemaining(prev => ({
        ...prev,
        [playerName]: count
      }));
      // Always update max for this turn when server sends it (e.g. 2 for first player main turn, 1 for deferred turn)
      if (maxForTurn !== undefined && maxForTurn !== null && setMaxActionsThisTurn) {
        const maxNum = Number(maxForTurn);
        if (!Number.isNaN(maxNum)) {
          setMaxActionsThisTurn(prev => ({
            ...prev,
            [playerName]: maxNum
          }));
        }
      }
    };

    const handleStockOwnershipUpdate = (counts) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] stock_ownership_update received');
      }
      setStockOwnershipCounts(counts || {});
    };

    const handleStockOwnershipByPlayerUpdate = (ownership) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] stock_ownership_by_player_update received');
      }
      setStockOwnershipByPlayer(ownership || {});
    };

    const handleIndexOwnershipByPlayerUpdate = (ownership) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] index_ownership_by_player_update received');
      }
      setIndexOwnershipByPlayer(ownership || {});
    };

    const handleNetWorthUpdate = (netWorths) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] net_worth_update received');
      }
      setPlayerNetWorths(netWorths || {});
    };

    currentSocket.on("purchase_result", handlePurchaseResult);
    currentSocket.on("sell_result", handleSellResult);
    currentSocket.on("purchase_index_result", handlePurchaseIndexResult);
    currentSocket.on("sell_index_result", handleSellIndexResult);
    currentSocket.on("portfolio_update", handlePortfolioUpdate);
    currentSocket.on("index_portfolio_update", handleIndexPortfolioUpdate);
    currentSocket.on("actions_update", handleActionsUpdate);
    currentSocket.on("stock_ownership_update", handleStockOwnershipUpdate);
    currentSocket.on("stock_ownership_by_player_update", handleStockOwnershipByPlayerUpdate);
    currentSocket.on("index_ownership_by_player_update", handleIndexOwnershipByPlayerUpdate);
    currentSocket.on("net_worth_update", handleNetWorthUpdate);

    return () => {
      currentSocket.off("purchase_result", handlePurchaseResult);
      currentSocket.off("sell_result", handleSellResult);
      currentSocket.off("purchase_index_result", handlePurchaseIndexResult);
      currentSocket.off("sell_index_result", handleSellIndexResult);
      currentSocket.off("portfolio_update", handlePortfolioUpdate);
      currentSocket.off("index_portfolio_update", handleIndexPortfolioUpdate);
      currentSocket.off("actions_update", handleActionsUpdate);
      currentSocket.off("stock_ownership_update", handleStockOwnershipUpdate);
      currentSocket.off("stock_ownership_by_player_update", handleStockOwnershipByPlayerUpdate);
      currentSocket.off("index_ownership_by_player_update", handleIndexOwnershipByPlayerUpdate);
      currentSocket.off("net_worth_update", handleNetWorthUpdate);
    };
  }, [socket, setPlayerPortfolios, setPlayerIndexPortfolios, setActionsRemaining, setMaxActionsThisTurn, setStockOwnershipCounts, setStockOwnershipByPlayer, setIndexOwnershipByPlayer, setPlayerNetWorths, showMessage, addNotification, setLoggedIn]);
};

